import Component from '@glimmer/component'
import type RouterService from '@ember/routing/router-service'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { MathseedsPrimeThread, isMathseedsPrimeThread } from 'teacher-ui-client/progression-sequence/utils'
import type { IntlService } from 'ember-intl'
import type Features from 'teacher-ui-client/services/features'
import { Pill, PILL_APPEARANCE } from 'teacher-ui-client/components/base/pill'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import themeKey from 'teacher-ui-client/helpers/theme-key'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { t } from 'ember-intl'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'

const { Number, Fractions, Operations, Geometry, Measurement } = MathseedsPrimeThread

export interface ThreadFilter {
  id: MathseedsPrimeThread
  label: string
  selected: boolean
}

export function deserializeThreads(serializedThreads: string) {
  return serializedThreads.split(',').filter(isMathseedsPrimeThread)
}

export function serializeThreads(threads: MathseedsPrimeThread[]) {
  return threads.join(',')
}

interface Signature {
  Element: HTMLDivElement
}

export class MathseedsPrimeThreadFilter extends Component<Signature> {
  @service router!: RouterService

  @service intl!: IntlService

  @service features!: Features

  get threadFilters(): ThreadFilter[] {
    const threads = this.features.isEnabled('tui_ms_prime_measurement_thread')
      ? [Number, Fractions, Operations, Geometry, Measurement]
      : [Number, Fractions, Operations, Geometry]
    return threads.map((thread) => ({
      id: thread,
      label: this.intl.t(`mathseedsPrimeThreads.${thread}.longName`),
      selected: this.selectedThreads.includes(thread),
    }))
  }

  get selectedThreads() {
    const filterThread = this.router.currentRoute?.queryParams.filterThread
    return deserializeThreads(typeof filterThread === 'string' ? filterThread : '')
  }

  @action
  toggleFilter({ id: toggleThread }: ThreadFilter) {
    let newThreads
    if (this.selectedThreads.includes(toggleThread)) {
      newThreads = this.selectedThreads.filter((selectedThread) => selectedThread !== toggleThread)
    } else {
      newThreads = [...this.selectedThreads, toggleThread]
    }
    void this.router.transitionTo({
      queryParams: { filterThread: serializeThreads(newThreads), pageNumber: 1 },
    })
  }

  <template>
    <div class="flex items-center justify-center space-x-2" ...attributes>
      <span class="italic">{{t "filter"}}:</span>
      {{#each this.threadFilters as |threadFilter|}}
        <Pill
          data-test-thread-filter={{threadFilter.id}}
          data-test-thread-filter-selected={{threadFilter.selected}}
          class="flex items-center px-5 py-2 font-semibold {{unless threadFilter.selected 'shadow-lg'}}"
          @theme={{themeKey SubscriptionType.Maths}}
          @appearance={{PILL_APPEARANCE.outline}}
          @selected={{threadFilter.selected}}
          {{on "click" (fn this.toggleFilter threadFilter)}}
        >
          <span>{{threadFilter.label}}</span>
          {{#if threadFilter.selected}}
            <PhosphorIcon @icon="phosphor-x-bold" class="ml-2" />
          {{/if}}
        </Pill>
      {{/each}}
    </div>
  </template>
}

export default MathseedsPrimeThreadFilter
